import { createContext, useCallback, useContext } from "react";
import type { Permission } from "@dentacore/shared-types";

import { atLocation } from "../api/client";

/**
 * **The location a booking belongs to, while its panel is open** (CF-D15,
 * CF-D9).
 *
 * With several locations in one calendar, the side panel acts on a booking
 * that may not be at the active location. Everything inside this provider
 * then works *there*: `useCan` answers with the permissions held at that
 * location, a figure is formatted in its currency, and every request made
 * through `useAtBookingLocation` carries its id. Outside a provider — the
 * single-location calendar, every other screen — nothing changes.
 */
export interface BookingLocation {
  branchId: string;
  name: string;
  /** The caller's access at this location, from the combined read. */
  permissions: readonly Permission[];
}

const Context = createContext<BookingLocation | null>(null);

export const BookingLocationProvider = Context.Provider;

/** The booking's location while its panel is open, or null on a single-location calendar. */
export function useBookingLocation(): BookingLocation | null {
  return useContext(Context);
}

/**
 * Run an API call at the booking's own location. Wrap the call itself —
 * `at(() => changeAppointmentStatus(id, body))` — so its request starts inside
 * the scope (see `atLocation`).
 */
export function useAtBookingLocation(): <T>(call: () => T) => T {
  const location = useContext(Context);
  const branchId = location?.branchId ?? null;
  return useCallback(<T,>(call: () => T): T => atLocation(branchId, call), [branchId]);
}

/**
 * The same, as a function wrapper: `here(changeAppointmentStatus)(id, body)`.
 * Each call is scoped on its own, so an async flow that makes several requests
 * keeps the location for every one of them.
 */
export function useBookingScoped(): <A extends unknown[], R>(
  fn: (...args: A) => R,
) => (...args: A) => R {
  const location = useContext(Context);
  const branchId = location?.branchId ?? null;
  return useCallback(
    <A extends unknown[], R>(fn: (...args: A) => R) =>
      (...args: A): R =>
        atLocation(branchId, () => fn(...args)),
    [branchId],
  );
}
